import { useEffect, useId } from 'react';
import { useIntl } from 'react-intl';
import { Eye } from 'lucide-react';
import Button from 'src/componentes/button';
import Drawer from 'src/componentes/drawer';
import Switch from 'src/componentes/Form/Switch';
import Input from 'src/componentes/input';
import CargoLookup from 'src/lookups/cargo';
import OrganizacaoLookup from 'src/lookups/item-organizacao';
import ColaboradorEGrupoSimplificadoLookup from 'src/lookups/ColaboradorEGrupoSimplificadoLookup';
import useAuth from 'src/hooks/useAuth';
import focusManager from 'src/utils/focus-manager';
import { capitalizeFirstLetter } from 'src/utils/string';
import PublicoInteressadoContador from './PublicoInteressadoContador';
import { CRITERIOS_PUBLICO_INTERESSADO_PADRAO } from './publicoInteressadoDocumento';

export default function PublicoInteressadoCriteriosDrawer({
  open,
  documentoId,
  criterios,
  onChange,
  onClose,
  onApply,
  onPreview,
  disabled,
  hasLift,
  hasCriterios,
  title,
  zIndex,
  focusTrapPriority,
}) {
  const intl = useIntl();
  const { terms } = useAuth();
  const fieldId = useId();

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (event) => {
      if (
        event.key === 'Escape' &&
        !event.defaultPrevented &&
        focusManager.getTopPriority() === focusTrapPriority
      ) {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose, focusTrapPriority]);

  const change = (field, nextValue) => {
    if (disabled) return;
    onChange({ ...criterios, [field]: nextValue });
  };
  const clear = () => {
    if (disabled) return;
    onChange({ ...CRITERIOS_PUBLICO_INTERESSADO_PADRAO });
  };
  const changeDays = (field, nextValue) => {
    if (nextValue === '') change(field, null);
    else if (/^\d+$/.test(nextValue) && Number(nextValue) <= 2147483647)
      change(field, Number(nextValue));
  };

  return (
    <Drawer
      open={open}
      close={onClose}
      title={
        <span className="tw:block tw:py-4 tw:text-xl tw:font-bold tw:text-foreground">
          {title}
        </span>
      }
      zIndex={zIndex}
      focusTrap
      focusTrapPriority={focusTrapPriority}
      actions={
        <div className="tw:flex tw:w-full tw:flex-wrap tw:items-center tw:justify-end tw:gap-2 tw:border-t tw:border-border tw:pt-4">
          {!disabled && (
            <Button
              type="button"
              color="link"
              className="tw:mr-auto tw:px-0 tw:text-base tw:font-bold"
              onClick={clear}
            >
              {intl.formatMessage({ id: 'limparTodos' })}
            </Button>
          )}
          <Button type="button" color="secondary" onClick={onClose}>
            {intl.formatMessage({
              id: disabled ? 'label.fechar' : 'label.cancelar',
            })}
          </Button>
          {!disabled && (
            <Button type="button" onClick={onApply}>
              {intl.formatMessage({ id: 'aplicar' })}
            </Button>
          )}
        </div>
      }
    >
      <div className="tw:-mx-1 tw:h-full tw:overflow-y-auto tw:px-1 tw:pb-4">
        <p className="tw:mb-4 tw:text-sm tw:text-muted-foreground">
          {intl.formatMessage({ id: 'msgPublicoInteressadoPorDocumento' })}
        </p>
        <fieldset
          disabled={disabled}
          className="tw:m-0 tw:min-w-0 tw:border-0 tw:p-0"
        >
          <legend className="tw:sr-only">{title}</legend>
          <div className="tw:grid tw:gap-4">
            {hasLift && (
              <div>
                <label htmlFor={`${fieldId}-cargo`}>
                  {intl.formatMessage(
                    { id: 'labelTempoMinimoCargoDias' },
                    { cargo: terms.cargo }
                  )}
                </label>
                <Input
                  id={`${fieldId}-cargo`}
                  type="number"
                  min="0"
                  max="2147483647"
                  step="1"
                  disabled={disabled}
                  showLabel={false}
                  model={{
                    value:
                      criterios.tempoMinimoCargoDias == null
                        ? ''
                        : String(criterios.tempoMinimoCargoDias),
                    requestChange: (value) =>
                      changeDays('tempoMinimoCargoDias', value),
                  }}
                />
              </div>
            )}
            <div>
              <label htmlFor={`${fieldId}-empresa`}>
                {intl.formatMessage({ id: 'labelTempoMinimoEmpresaDias' })}
              </label>
              <Input
                id={`${fieldId}-empresa`}
                type="number"
                min="0"
                max="2147483647"
                step="1"
                disabled={disabled}
                showLabel={false}
                model={{
                  value:
                    criterios.tempoMinimoEmpresaDias == null
                      ? ''
                      : String(criterios.tempoMinimoEmpresaDias),
                  requestChange: (value) =>
                    changeDays('tempoMinimoEmpresaDias', value),
                }}
                aria-describedby={`${fieldId}-admissao`}
              />
              <p
                id={`${fieldId}-admissao`}
                className="tw:mt-1 tw:text-sm tw:text-muted-foreground"
              >
                {intl.formatMessage(
                  { id: 'msgTempoEmpresaDataAdmissao' },
                  { colaborador: terms.colaborador }
                )}
              </p>
            </div>
            {hasLift && (
              <CargoLookup
                multi
                disabled={disabled}
                showNew={false}
                model={{
                  label: capitalizeFirstLetter(terms.cargos),
                  value: criterios.cargos ?? [],
                  requestChange: (items) => change('cargos', items ?? []),
                }}
              />
            )}
            <div>
              <OrganizacaoLookup
                multi
                disabled={disabled}
                showNew={false}
                model={{
                  label: capitalizeFirstLetter(terms.areas),
                  value: criterios.areas ?? [],
                  requestChange: (items) => {
                    if (disabled) return;
                    onChange({
                      ...criterios,
                      areas: items ?? [],
                      considerarAreasSubordinadas:
                        items?.length > 0 &&
                        criterios.considerarAreasSubordinadas,
                    });
                  },
                }}
              />
              <Switch
                id={`${fieldId}-subordinadas`}
                role="switch"
                disabled={disabled || !criterios.areas?.length}
                checked={!!criterios.considerarAreasSubordinadas}
                onChange={(event) =>
                  change('considerarAreasSubordinadas', event.target.checked)
                }
                label={intl.formatMessage(
                  { id: 'labelConsiderarAreasSubordinadasPublico' },
                  { areas: terms.areas }
                )}
              />
            </div>
            <div className="tw:min-w-0">
              <ColaboradorEGrupoSimplificadoLookup
                abrirEmDrawer
                labelComoTitulo
                compactMulti
                multi
                disabled={disabled}
                showNew={false}
                data={{ somenteAtivos: true }}
                model={{
                  label: capitalizeFirstLetter(terms.colaboradores),
                  value: criterios.colaboradores ?? [],
                  requestChange: (items) =>
                    change('colaboradores', items ?? []),
                }}
              />
            </div>
          </div>
        </fieldset>

        <p className="tw:my-3 tw:text-sm tw:text-muted-foreground">
          {intl.formatMessage(
            { id: 'msgCombinacaoCriteriosPublicoInteressado' },
            { colaboradores: terms.colaboradores }
          )}
        </p>
        <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
          <Button
            type="button"
            color="secondary"
            leftIcon={Eye}
            disabled={!hasCriterios}
            onClick={onPreview}
          >
            {intl.formatMessage({ id: 'labelPrevisualizarPublicoInteressado' })}
          </Button>
          <PublicoInteressadoContador
            documentoId={documentoId}
            criterios={criterios}
            hasLift={hasLift}
            hasCriterios={hasCriterios}
            enabled={open}
          />
        </div>
        {!hasCriterios && (
          <p className="tw:mt-3 tw:text-sm tw:text-muted-foreground">
            {intl.formatMessage(
              { id: 'msgSelecioneCriterioPublicoInteressado' },
              { colaboradores: terms.colaboradores }
            )}
          </p>
        )}
      </div>
    </Drawer>
  );
}
